import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'nl/gericht-verkeer-kopen',
    titleTag: 'Buy Targeted Traffic | Country & Niche Targeting | Traffic Creator',
    metaDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience. Verified in GA4.',
    keywords: 'Buy Targeted Traffic, Buy Targeted Website Traffic, Buy Targeted Web Traffic, Targeted Traffic That Converts, Buy Keyword Targeted Traffic',
    primaryKeyword: 'Buy Targeted Traffic',
    secondaryKeyword: 'Targeted Traffic That Converts',
    reviewCount: '518',
    ratingValue: '4.9',

    h1: 'Buy Targeted Traffic That Converts into Real Results',
    subheadline: 'Generic traffic is wasted budget. Our platform lets you buy targeted traffic from exact countries, devices, and referrer paths — matching your ideal audience profile perfectly.',

    problemH2: 'Why Untargeted Traffic Wastes Your Budget',
    problemText: 'Sending random visitors from irrelevant countries and devices to your website creates terrible bounce rates and zero conversions. You need traffic that matches your actual audience — the right geography, the right device, and the right browsing behavior.',
    solutionPoints: [
        'Precision geo-targeting from 190+ countries down to city level',
        'Device targeting — desktop, mobile, or tablet traffic on demand',
        'Custom referrer paths to simulate traffic from Google, social media, or direct',
        'Keyword-targeted sessions that arrive via search-like pathways'
    ],

    featuresH2: 'How Our Targeted Traffic Platform Works',
    features: [
        { icon: <Globe />, title: 'Country & City Targeting', description: 'Choose traffic from any country worldwide. Need visitors specifically from New York, London, or Tokyo? Our residential network delivers with pinpoint accuracy.' },
        { icon: <BarChart3 />, title: 'Behavioral Targeting', description: 'Configure exactly how visitors interact — dwell time, pages per session, scroll depth, and bounce rate — to match your niche\'s natural engagement patterns.' },
        { icon: <Activity />, title: 'Referrer Source Control', description: 'Make traffic appear as if it came from Google search, social media, or direct visits. Full control over the referrer path for realistic Analytics data.' }
    ],

    testimonialsH2: 'What Our Targeted Traffic Clients Say',
    testimonials: [
        { name: 'David L.', role: 'E-commerce Director', result: 'USA Traffic +400%', text: 'We needed US-only traffic for our Shopify store. The geo-targeting accuracy was incredible — 98% of visits came from our target states.', seed: 'davidl' },
        { name: 'Nina S.', role: 'Media Buyer', result: 'Perfect Device Mix', text: 'Being able to buy targeted traffic split 70/30 mobile/desktop matched our actual audience perfectly. The Analytics looked completely natural.', seed: 'ninas' },
        { name: 'Carlos M.', role: 'Local Business Owner', result: 'City-Level Precision', text: 'I bought keyword targeted traffic for my local service area. All visits came from my city and neighboring areas. Incredible precision.', seed: 'carlosm' }
    ],

    ctaH2: 'Ready to Buy Targeted Traffic That Matches Your Audience?',

    faqs: [
        { question: 'What does buy targeted traffic mean exactly?', answer: 'Buying targeted traffic means purchasing website visitors that match specific criteria — geographic location, device type, referrer source, and behavioral patterns. Unlike generic traffic, targeted traffic aligns with your actual audience profile.' },
        { question: 'Can I buy targeted traffic from a specific country?', answer: 'Yes! We offer geo-targeting from 190+ countries. You can target by country, region, or even city level. Our residential IP network ensures every visit genuinely originates from your chosen location.' },
        { question: 'Does buying targeted traffic that converts actually work?', answer: 'The traffic itself generates realistic engagement signals (dwell time, scrolling, page views) that improve your site\'s SEO metrics. For e-commerce conversions, it helps by improving your search rankings which leads to more genuine converting visitors.' },
        { question: 'How is keyword targeted traffic different?', answer: 'Keyword targeted traffic arrives at your site through search-like pathways, mimicking visitors who found you through specific search queries. This creates organic search signals that can improve your rankings for those exact keywords.' },
        { question: 'Will targeted traffic appear in Google Analytics?', answer: 'Yes, 100%. All targeted traffic registers in GA4 with accurate geographic data, device information, session duration, and referrer paths — exactly matching the targeting parameters you set.' }
    ],

    relatedLinks: [
        { label: 'Geo-Targeted Traffic', to: '/nl/geo-gericht-verkeer' },
        { label: 'Buy Website Traffic', to: '/nl/website-verkeer-kopen' },
        { label: 'Local SEO Traffic Bot', to: '/nl/lokale-seo-verkeer-bot' }
    ],

    productName: 'Buy Targeted Traffic - Precision Audience Matching',
    productDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience.',
    translations: {
        'navFeatures': 'Functies',
        'navPricing': 'Prijzen',
        'navFaq': 'FAQ',
        'navLogin': 'Dashboard Login',
        'heroBadge': 'Premium Verkeer Service',
        'heroCtaPrimary': 'Gratis Uitproberen',
        'heroCtaSecondary': 'Prijzen Bekijken',
        'heroTrust': 'Vertrouwd door meer dan 10.000 gebruikers',
        'heroMicrocopy': 'Geen creditcard nodig. 100% veilig & analytics-geverifieerd.',
        'heroLiveLabel': 'Live',
        'heroTrafficIncrease': 'Verkeersstijging',
        'heroActiveLabel': 'Actief',
        'heroVisitorsServed': 'Bezoekers Geleverd',
        'socialProofLabel': 'Vertrouwd door:',
        'problemWithout': 'Zonder',
        'problemWith': 'Met',
        'problemBullets': [
                'Laag organisch verkeer beperkt uw groei',
                'Slechte zoekresultaten schaden conversies',
                'Concurrenten domineren uw zoekwoorden',
                'Hoge bouncepercentages schaden uw SEO'
        ],
        'statsCampaigns': 'Dagelijks Actieve Campagnes',
        'statsCountries': 'Landen',
        'statsUptime': 'Uptime',
        'statsSupport': 'Ondersteuning',
        'featuresSubtitle': 'Alles wat u nodig heeft om de prestaties van uw website te verbeteren.',
        'howItWorksH2': '3 Eenvoudige Stappen',
        'howItWorksSteps': [
                {
                        'title': 'Campagne Configureren',
                        'desc': 'Kies land, apparaat en gedragsinstellingen in enkele minuten.'
                },
                {
                        'title': 'Netwerk Starten',
                        'desc': 'Leun achterover terwijl ons platform echte bezoekers naar uw URL leidt.'
                },
                {
                        'title': 'Resultaten Analyseren',
                        'desc': 'Bekijk hoe uw statistieken verbeteren in Google Analytics 4.'
                }
        ],
        'pricingH2': 'Transparante',
        'pricingSubtitle': 'Eenmalige aankoop. Geen abonnementen. Credits verlopen nooit.',
        'pricingMostPopular': 'Populairst',
        'pricingVisitors': 'bezoekers',
        'pricingGetStarted': 'Nu Starten',
        'pricingGuarantee': '30 Dagen Geld-terug-garantie',
        'pricingDelivery': 'Directe Levering (~5 min)',
        'pricingCountries': '190+ Landen',
        'faqH2': 'Veelgestelde Vragen',
        'ctaSubtitle': 'Sluit u aan bij meer dan 10.000 klanten en boost uw websiteverkeer vandaag nog.',
        'ctaButtonPrimary': 'Gratis Starten',
        'ctaButtonSecondary': 'Prijzen Bekijken',
        'ctaMicrocopy': 'Geen creditcard nodig. 100% veilig & analytics-geverifieerd.',
        'footerDescription': 'oplossingen voor SEO-professionals en digitale bureaus wereldwijd.',
        'footerRelatedServices': 'Gerelateerde Diensten',
        'footerCompany': 'Bedrijf',
        'footerAbout': 'Over Ons',
        'footerBlog': 'Blog',
        'footerHelpCenter': 'Helpcentrum',
        'footerPrivacy': 'Privacybeleid',
        'footerImpressum': 'Impressum',
        'footerStatus': 'Alle Systemen Operationeel',
        'footerBuyTraffic': 'Website Verkeer Kopen'
},
};

const NlBuyTargetedTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default NlBuyTargetedTraffic;
